// src/pages/reports/LoginOtp/ReportsLogTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Select,
} from "@mui/material";
import PaginationReports from "../../brands/PaginationReports";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import Export from "../../../../src/assests/images/export.svg";
import * as XLSX from "xlsx";
import { getToken } from "../../../utils/utils";
import { exportRetailersAndAccounts, exportRetailersAndAccounts_csv } from "../api";

/** RewardsTable-style rotating arrow */
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right:"10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
    }}
  />
);

type Order = "asc" | "desc";

interface ReportsLogTableProps {
  searchQuery: string;
  dateRange: [Date | null, Date | null];
  appliedFilters?: {
    locations?: string[];
    outlet_category?: string[];
    device_type?: string[];
    pmi_entity_code?: string[];
    outlet_name?: string[];
    lastLogin?: { from: string; to: string };
  } | null;
}

const RAW_COLUMNS = [
  { label: "User ID", value: "user_id" },
  { label: "User Salesforce ID", value: "user_salesforce_id" },
  { label: "PMI Entity Code", value: "pmi_entity_code" },
  { label: "User First Name", value: "user_first_name" },
  { label: "User Last Name", value: "user_last_name" },
  { label: "Login Date", value: "login_date" },
  { label: "Outlet Name", value: "outlet_name" },
  { label: "City", value: "city" },
  { label: "Outlet Category", value: "outlet_category" },
  { label: "Phone Number", value: "phone_number" },
  { label: "Login Time", value: "login_time" },
  { label: "OTP Trigger Success", value: "otp_trigger_success" },
  { label: "Device OS", value: "device_os" },
  { label: "Browser Name", value: "browser_name" },
] as const;

type ColumnLabel = (typeof RAW_COLUMNS)[number]["label"];
type ColumnKey = (typeof RAW_COLUMNS)[number]["value"];

const ALL_LABELS: ColumnLabel[] = RAW_COLUMNS.map((c) => c.label);
const labelToKey = RAW_COLUMNS.reduce<Record<ColumnLabel, ColumnKey>>((acc, c) => {
  acc[c.label] = c.value;
  return acc;
}, {} as any);

const DEFAULT_VISIBLE: ColumnLabel[] = ALL_LABELS.slice(0, 6);
const DEFAULT_SET = new Set<ColumnLabel>(DEFAULT_VISIBLE);

const withDefaultsIfEmpty = (arr: ColumnLabel[]) =>
  arr && arr.length ? arr : [...DEFAULT_VISIBLE];

/** ✅ Format: 13-Oct-2025 (handles bad/empty values gracefully) */
const formatDDMonYYYY = (value: any) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day}-${mon}-${year}`;
};

const ReportsLogTable: React.FC<ReportsLogTableProps> = ({
  searchQuery,
  dateRange: parentDateRange,
  appliedFilters = null,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  // 🔹 paging
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // <— NEW
  const totalPages = Math.ceil(totalRows / itemsPerPage);

  // RewardsTable-style column picker
  const [visibleColumns, setVisibleColumns] = useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] =
    useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [colSelectOpen, setColSelectOpen] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  // sorting (use backend keys)
  const [orderBy, setOrderBy] = useState<ColumnKey>("user_id");
  const [order, setOrder] = useState<Order>("asc");

  // local date range snapshot (same behavior as RewardsTable)
  const [localDateRange] = useState<[Date | null, Date | null]>(parentDateRange);

  const filteredColumns = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) return ALL_LABELS;
    return ALL_LABELS.filter((name) => name.toLowerCase().includes(q));
  }, [colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 &&
    filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      setTempSelectedColumns((prev) => {
        const next = prev.filter((p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p));
        DEFAULT_VISIBLE.forEach((d) => { if (!next.includes(d)) next.push(d); });
        return next;
      });
    } else {
      setTempSelectedColumns((prev) =>
        Array.from(new Set<ColumnLabel>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE]))
      );
    }
  };

  // Build identical payload for list + export
  const buildPayload = () => {
    const [start, end] = localDateRange;
    return {
      page,
      limit: itemsPerPage, // 🔹 dynamic page size
      ...(searchQuery ? { search: searchQuery } : {}),
      ...(start && end ? { lastLogin: { from: start.toISOString(), to: end.toISOString() } } : {}),
      ...(appliedFilters?.locations?.length ? { locations: appliedFilters.locations } : {}),
      ...(appliedFilters?.outlet_category?.length ? { outlet_category: appliedFilters.outlet_category } : {}),
      ...(appliedFilters?.device_type?.length ? { device_type: appliedFilters.device_type } : {}),
      ...(appliedFilters?.pmi_entity_code?.length ? { pmi_entity_code: appliedFilters.pmi_entity_code } : {}),
      ...(appliedFilters?.outlet_name?.length ? { outlet_name: appliedFilters.outlet_name } : {}),
    };
  };

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const payload = buildPayload();
        const response = await exportRetailersAndAccounts(payload, token!);

        const resultsArray = response?.results ?? [];
        const totalRowsValue = response?.total_rows ?? 0;

        const normalized = resultsArray.map((r: any) => ({
          user_id: r.user_id ?? "",
          user_salesforce_id: r.user_salesforce_id ?? "",
          pmi_entity_code: r.pmi_entity_code ?? "",
          user_first_name: r.user_first_name ?? "",
          user_last_name: r.user_last_name ?? "",
          outlet_name: r.outlet_name ?? "",
          city: r.city ?? "",
          outlet_category: r.outlet_category ?? "",
          phone_number: r.phone_number ?? "",
          login_date: r.login_date ?? "",
          login_time: r.login_time ?? "",
          otp_trigger_success: r.otp_trigger_success ?? "",
          device_os: r.device_os ?? "",
          browser_name: r.browser_name ?? "",
        }));

        setRows(normalized);
        setTotalRows(totalRowsValue);
      } catch (err) {
        console.error("Failed to fetch login report:", err);
        setRows([]);
        setTotalRows(0);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, itemsPerPage, localDateRange, appliedFilters]); // 🔹 refetch on size change

  const handleSort = (label: ColumnLabel) => {
    const key = labelToKey[label];
    const isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];

    copy.sort((a, b) => {
      // ✅ Proper date sorting for Login Date
      if (orderBy === "login_date") {
        const ta = Date.parse(a?.login_date ?? "");
        const tb = Date.parse(b?.login_date ?? "");
        const cmp = ((isNaN(ta) ? -Infinity : ta) - (isNaN(tb) ? -Infinity : tb));
        return order === "asc" ? cmp : -cmp;
      }

      // ✅ Natural, numeric-aware compare for everything else (fixes "1, 10, 2")
      const aStr = String(a?.[orderBy] ?? "");
      const bStr = String(b?.[orderBy] ?? "");
      const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
      return order === "asc" ? cmp : -cmp;
    });

    return copy;
  }, [rows, orderBy, order]);

  // CSV export (visible columns only)
  const handleExport = async () => {
    try {
      const token = getToken();
      const selectedKeys: ColumnKey[] = visibleColumns.map((l) => labelToKey[l]);

      const payload: any = {
        ...buildPayload(),
        columns: selectedKeys,
        limit: "all",
        export: true,
      };

      const response = await exportRetailersAndAccounts_csv(payload, token!, true);

      if (response instanceof Blob) {
        const blobType = response.type || "application/octet-stream";
        if (blobType.includes("csv") || blobType.includes("text")) {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `login_otp_report_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          return;
        }

        // Excel → CSV fallback
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName]);

        const csvBlob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `login_otp_report_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #d1cfcf",
        borderRadius: "10px",
        margin: "30px 32px 100px",
      }}
    >
      {/* Header — same layout as RewardsTable */}
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalRows} Users
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Column picker with tooltip + search + select-all */}
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={tempSelectedColumns.length ? tempSelectedColumns.join(", ") : "Select Columns"}
              placement="top"
              arrow
              PopperProps={{
                modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
              }}
            >
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next);
                    setVisibleColumns(next);
                    setColSearch("");
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr =
                      typeof val === "string" ? (val as string).split(",") : (val as ColumnLabel[]);
                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                      toggleSelectAllOnFiltered();
                      return;
                    }
                    const next = withDefaultsIfEmpty(Array.isArray(arr) ? (arr as ColumnLabel[]) : []);
                    setTempSelectedColumns(next);
                  }}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  IconComponent={() => (
                    <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: "250px",
                        overflowY: "auto",
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" }, // hide default icon
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  {/* sticky search bar */}
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, lineHeight: 1, px: 1 }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  {/* Select all (filtered) */}
                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              position: "relative",
              left: "-7px",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Table — same header bg & minWidth logic */}
      <TableContainer
        component={Paper}
        sx={{ borderTop: "1px solid #d3d3d3", boxShadow: "none", maxWidth: "100%", overflowX: "auto" }}
      >
        <Table sx={{ minWidth: visibleColumns.length * 150 }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((label) => {
                const key = labelToKey[label];
                return (
                  <TableCell
                    key={label}
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 300,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleSort(label)}
                      IconComponent={() => <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <TableRow key={row.user_id ?? idx}>
                  {visibleColumns.map((label) => {
                    const key = labelToKey[label] as ColumnKey;
                    const raw = row[key];
                    let display: React.ReactNode = "-";

                    if (raw) {
                      if (key === "login_date") {
                        // ✅ show as 13-Oct-2025
                        display = formatDDMonYYYY(raw);
                      } else if (key === "login_time") {
                        display = String(raw).slice(0, 8);
                      } else {
                        display = String(raw);
                      }
                    }

                    return (
                      <TableCell
                        key={key}
                        sx={{
                          maxWidth: 260,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={typeof display === "string" ? (display as string) : undefined}
                      >
                        {display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination + rows-per-page (like ReportsMasterTable) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ width: 200 }} />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#555" }}>
            Rows per page
          </Typography>
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1); // reset to first page on size change
              }}
              sx={{ borderRadius: "8px", height: 36 }}
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportsLogTable;
